import { Link } from "react-router-dom";
import { ArrowLeft, Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSeoHeadTags } from "@/hooks/useSeoHeadTags";
import { usePublicData } from "@/hooks/usePublicData";
import { DEFAULT_PHONE, DEFAULT_EMAIL, SITE_ORIGIN } from "@/lib/publicSite";

const CookieTable = ({ rows }) => (
  <table className="w-full text-sm border-collapse">
    <thead>
      <tr className="border-b">
        <th className="text-left py-2">Name</th>
        <th className="text-left py-2">Purpose</th>
        <th className="text-left py-2">Duration</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r[0]} className="border-b">
          <td className="py-2 align-top font-mono text-xs">{r[0]}</td>
          <td className="py-2 align-top">{r[1]}</td>
          <td className="py-2 align-top">{r[2]}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

export default function CookiePolicy() {
  const { bizInfo } = usePublicData();
  const phone = bizInfo?.business_phone || DEFAULT_PHONE;
  const email = bizInfo?.business_email || DEFAULT_EMAIL;

  useSeoHeadTags({
    title: "Cookie Policy | Sustainable Handyman Services",
    description: "How Sustainable Handyman Services uses cookies and similar technologies, which cookies are set, and how to manage your choices.",
    canonical: `${SITE_ORIGIN}/cookies`,
    ogType: "website",
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <header className="bg-[#1e3a5f] text-white py-4">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80">
            <ArrowLeft className="h-5 w-5" />
            Back to Home
          </Link>
          <div className="flex items-center gap-2">
            <Cookie className="h-5 w-5" />
            <span className="font-semibold">Cookie Policy</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="prose prose-slate max-w-none">
          <h1 className="text-3xl font-bold text-[#1e3a5f] mb-2">Cookie Policy</h1>
          <p className="text-muted-foreground mb-2">Last updated: August 2026</p>
          <p className="text-sm text-muted-foreground mb-8 italic">
            This policy describes the cookies and similar technologies we have observed on this
            website. Final wording is pending owner/legal review.
          </p>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">1. What Are Cookies?</h2>
            <p>
              Cookies are small text files placed on your device when you visit a website. Similar
              technologies (such as browser local storage) store information in your browser
              without setting a cookie. This policy covers both.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">2. Cookies We Set</h2>

            <div className="bg-slate-50 rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-[#1e3a5f] mb-2">Essential (Strictly Necessary)</h3>
              <p className="text-sm mb-2">
                Needed for sign-in areas of the site to work. They are only set when you use those
                areas — ordinary visitors browsing the public site do not receive them.
              </p>
              <CookieTable rows={[
                ["admin_token", "Keeps the site administrator signed in to the admin portal. Set only after an admin signs in.", "7 days"],
                ["portal_token", "Keeps registered customers signed in to the customer portal. Set only after a customer signs in.", "7 days"],
              ]} />
            </div>

            <div className="bg-slate-50 rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-[#1e3a5f] mb-2">Your Cookie Choice (Local Storage)</h3>
              <p className="text-sm mb-2">
                Your consent choice is stored in your browser's local storage (not a cookie) under
                the key <span className="font-mono text-xs">cookie_consent</span>. It stays until
                you clear your browser data and is never sent to us or anyone else.
              </p>
            </div>

            <div className="bg-blue-50 rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-[#1e3a5f] mb-2">Analytics (Optional — only with your permission)</h3>
              <p className="text-sm mb-2">
                Our website platform includes PostHog, a third-party analytics tool. It only starts
                — and only sets its identifier — after you choose <strong>Accept All</strong> (or
                enable optional cookies in the preferences panel). If you choose{" "}
                <strong>Essential Only</strong>, it is not started; if you later change your choice
                from allowed to Essential Only, we stop collection and delete the PostHog
                identifiers this site controls.
              </p>
              <CookieTable rows={[
                ["ph_phc_… (PostHog)", "Anonymous visitor and session identifiers used for usage analytics (page views, sessions).", "Persists in your browser (observed up to about 1 year; exact duration is set by PostHog and may vary)"],
              ]} />
            </div>

            <div className="bg-amber-50 rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-[#1e3a5f] mb-2">Platform &amp; Security Cookies (not set by us)</h3>
              <p className="text-sm mb-2">
                Our hosting and security providers set the cookies below. We do not control them
                and cannot switch them off from this website. Their exact classification and
                ownership cannot be fully verified by us — this is flagged for owner/legal review.
              </p>
              <CookieTable rows={[
                ["__cf_bm", "Cloudflare bot-protection cookie that helps distinguish humans from automated traffic.", "About 30 minutes"],
                ["__emg_vid / __emg_sid", "Set by the Emergent hosting platform on the live site. They appear to be visitor/session identifiers; their precise purpose and classification is not something we can verify.", "Unknown / set by the platform"],
              ]} />
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">3. Your Choices</h2>
            <p>
              When you first visit, a banner lets you choose <strong>Accept All</strong>,{" "}
              <strong>Essential Only</strong>, or <strong>Manage</strong> individual preferences.
              You can review or change your choice at any time using the{" "}
              <strong>Cookie Settings</strong> link in the footer of every page, which reopens the
              same preferences panel.
            </p>
            <p className="mt-2">
              Choosing Essential Only stops optional analytics from starting. Please note we cannot
              remove the platform and security cookies listed above; those are controlled by the
              hosting and security providers.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">4. Managing Cookies in Your Browser</h2>
            <ul className="list-disc pl-6 mt-2 space-y-2">
              <li><strong>Chrome:</strong> Settings → Privacy and security → Cookies and other site data</li>
              <li><strong>Firefox:</strong> Settings → Privacy &amp; Security → Cookies and Site Data</li>
              <li><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
              <li><strong>Edge:</strong> Settings → Cookies and site permissions → Manage and delete cookies</li>
            </ul>
            <p className="mt-4">
              Blocking the essential sign-in cookies will prevent the admin and customer portals
              from working, but does not affect ordinary browsing of the public site.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">5. Changes to This Policy</h2>
            <p>
              We may update this Cookie Policy from time to time. Any changes will be posted on
              this page with an updated revision date.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">6. Contact Us</h2>
            <p>If you have any questions about our use of cookies, please contact us:</p>
            <p className="mt-2">
              Email: {email}<br />
              Phone: {phone}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-[#1e3a5f] mb-4">7. More Information</h2>
            <p>
              For more information about cookies and how to manage them, visit{" "}
              <a href="https://www.aboutcookies.org" target="_blank" rel="noopener noreferrer" className="text-[#1e3a5f] underline hover:no-underline">
                www.aboutcookies.org
              </a>{" "}
              or{" "}
              <a href="https://ico.org.uk/for-the-public/online/cookies/" target="_blank" rel="noopener noreferrer" className="text-[#1e3a5f] underline hover:no-underline">
                ICO guidance on cookies
              </a>.
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t">
          <Link to="/">
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </main>
    </div>
  );
}
